import { useNode, useEditor } from "@craftjs/core";
import React from "react";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  BoxShadowInput,
  PaddingInput,
  Panel,
  Tabs,
  BorderRadiusInput,
  BorderInput,
  Spacer,
} from "../../input";
import { isUpdatedFromDefault } from "../../utils";

export const DirectoryTemplateSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
  }));

  const {
    query: { node },
  } = useEditor();

  const childrens = node(id).childNodes();
  const parents = node(id).ancestors();

  const {
    align,
    textColor,
    backgroundColor,
    typoSize,
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoWeight,
    typoTransform,
    typoDecoration,
    typoStyle,
    border,
    borderTablet,
    borderMobile,
    borderRadius,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
    boxShadow,
    blockGap = "1rem",
  } = props;

  const JUSTIFY = [
    {
      label: "None",
      value: "none",
    },
    {
      label: "Start",
      value: "start",
    },
    {
      label: "Center",
      value: "center",
    },
    {
      label: "End",
      value: "end",
    },
  ];

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <UnitInput
            resetValue={"1rem"}
            value={blockGap}
            onChange={(val) => {
              setProp((props) => {
                props.blockGap = val;
              });
            }}
            label={"Gap"}
          />
        </Spacer>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: textColor },
              { key: backgroundColor },
            ])}
            id={`block-setting-${id}-colors`}
            title={"Colors"}
            initialOpen={true}
          >
            <ColorInput
              resetValue={undefined}
              value={textColor}
              onChange={(val) => {
                setProp((props) => {
                  props.textColor = val;
                });
              }}
              label={"Text"}
            />
            <ColorInput
              resetValue={undefined}
              value={backgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.backgroundColor = val;
                });
              }}
              label={"Background"}
            />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: typoSize },
              { key: typoLineHeight },
              { key: typoLetterSpacing },
              { key: typoWordSpacing },
              { key: typoWeight },
              { key: typoTransform },
              { key: typoDecoration },
              { key: typoStyle },
            ])}
            id={`block-setting-${id}-typography`}
            title={"Typography"}
            initialOpen={false}
          >
            <UnitInput
              resetValue={undefined}
              value={typoSize}
              onChange={(val) => {
                setProp((props) => {
                  props.typoSize = val;
                });
              }}
              label={"Size"}
            />
            <UnitInput
              resetValue={undefined}
              value={typoLineHeight}
              onChange={(val) => {
                setProp((props) => {
                  props.typoLineHeight = val;
                });
              }}
              label={"Line Height"}
            />
            <UnitInput
              resetValue={undefined}
              value={typoLetterSpacing}
              onChange={(val) => {
                setProp((props) => {
                  props.typoLetterSpacing = val;
                });
              }}
              label={"Letter Spacing"}
            />
            <UnitInput
              resetValue={undefined}
              value={typoWordSpacing}
              onChange={(val) => {
                setProp((props) => {
                  props.typoWordSpacing = val;
                });
              }}
              label={"Word Spacing"}
            />
            <SelectInput
              resetValue={undefined}
              value={typoWeight}
              onChange={(val) => {
                setProp((props) => {
                  props.typoWeight = val;
                });
              }}
              options={[
                { label: "100", value: "100" },
                { label: "200", value: "200" },
                { label: "300", value: "300" },
                { label: "400", value: "400" },
                { label: "500", value: "500" },
                { label: "600", value: "600" },
                { label: "700", value: "700" },
                { label: "800", value: "800" },
                { label: "900", value: "900" },
              ]}
              label="Weight"
            />
            <SelectInput
              resetValue={undefined}
              value={typoTransform}
              onChange={(val) => {
                setProp((props) => {
                  props.typoTransform = val;
                });
              }}
              options={["uppercase", "lowercase", "capitalize", "none"].map(
                function (item) {
                  return {
                    label: item,
                    value: item,
                  };
                },
              )}
              label="Transform"
            />
            <SelectInput
              resetValue={undefined}
              value={typoDecoration}
              onChange={(val) => {
                setProp((props) => {
                  props.typoDecoration = val;
                });
              }}
              options={[
                { label: "Underline", value: "underline" },
                { label: "Overline", value: "overline" },
                { label: "None", value: "none" },
              ]}
              label="Decoration"
            />
            <SelectInput
              resetValue={undefined}
              value={typoStyle}
              onChange={(val) => {
                setProp((props) => {
                  props.typoStyle = val;
                });
              }}
              options={[
                { label: "Normal", value: "normal" },
                { label: "Italic", value: "italic" },
              ]}
              label="Style"
            />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutPadding },
            { key: layoutPaddingTablet },
            { key: layoutPaddingMobile },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <PaddingInput
            resetValue={undefined}
            value={layoutPadding}
            isResponsive={true}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderTablet },
            { key: borderMobile },
            { key: borderRadius },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={false}
        >
          <BorderInput
            value={border}
            onChange={(val) => {
              setProp((props) => {
                props.border = val;
              });
            }}
            label={"Border"}
            isResponsive={true}
            valueTablet={borderTablet}
            setValueTablet={(val) => {
              setProp((props) => {
                props.borderTablet = val;
              });
            }}
            valueMobile={borderMobile}
            setValueMobile={(val) => {
              setProp((props) => {
                props.borderMobile = val;
              });
            }}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={borderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.borderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: boxShadow }])}
          id={`block-setting-${id}-box-shadow`}
          title={"Box Shadow"}
          initialOpen={false}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={boxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.boxShadow = val;
              });
            }}
          />
        </Panel>
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([{ key: blockGap }]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: textColor },
        { key: backgroundColor },
        { key: typoSize },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoWeight },
        { key: typoTransform },
        { key: typoDecoration },
        { key: typoStyle },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutPadding },
        { key: layoutPaddingTablet },
        { key: layoutPaddingMobile },
        { key: border },
        { key: borderTablet },
        { key: borderMobile },
        { key: borderRadius },
        { key: boxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
